import React, { useEffect, useRef, useState } from 'react';
import type { IntakeFormField } from '../../api/bookingApi';

// --- Types ---

export interface UploadMeta {
  fileName: string;
  fileType: string;
  previewUrl?: string;
}

/** What a file field accepts: mime types, size cap, and the copy shown for each. */
export interface FileUploadPolicy {
  mimeTypes: string[];
  accept: string;
  maxBytes: number;
  helpText: string;
  typeError: string;
  sizeError: string;
}

/**
 * Where a picked file goes: form-package v2 injects a stager that returns the
 * staged `upload_id` as the field value (plus any vision field mapping).
 */
export type FileUploader = (file: File) => Promise<{
  value: unknown;
  fieldMapping?: Record<string, string> | null;
}>;

// --- File Upload Field ---
//
// Renderer for `field_type: 'file_upload'` intake fields (photo ID, insurance
// card), mounted by the form-package v2 section step with its staging uploader
// and policy. The V1 intake / kiosk / package renderers that also mounted it,
// and the default uploader they relied on, were retired with
// operaitor/backend#1604 slice 2.

export function FileUploadField({
  field,
  value,
  onChange,
  uploadMeta,
  onUploadMetaChange,
  onUploadMetaRemove,
  onAutoFill,
  policy,
  uploader,
  preprocess,
  captureActions = false,
}: {
  field: IntakeFormField;
  value: unknown;
  onChange: (v: unknown) => void;
  uploadMeta: Record<string, UploadMeta>;
  onUploadMetaChange: (key: string, meta: UploadMeta) => void;
  onUploadMetaRemove: (key: string) => void;
  onAutoFill?: (mapping: Record<string, string>) => void;
  policy: FileUploadPolicy;
  uploader: FileUploader;
  /**
   * Optional transform (e.g. downscale/transcode a phone photo) applied after
   * the type check and before the size check + upload. Absent = today's path.
   */
  preprocess?: (file: File) => Promise<File>;
  /** On phones, replaces the drag-drop dropzone with Take photo / Choose a file
   *  actions driving the same hidden input (`capture` toggled per action).
   *  Default false = today's dropzone everywhere. */
  captureActions?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const meta = uploadMeta[field.key];

  useEffect(() => {
    return () => {
      if (meta?.previewUrl) URL.revokeObjectURL(meta.previewUrl);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleFile(file: File) {
    setUploadError('');
    if (!policy.mimeTypes.includes(file.type)) {
      setUploadError(policy.typeError);
      return;
    }
    // Without a preprocess step the size check stays synchronous, before the
    // spinner — exactly the legacy behavior. With one, size is judged on the
    // transformed file inside the uploading state.
    if (!preprocess && file.size > policy.maxBytes) {
      setUploadError(policy.sizeError);
      return;
    }
    setUploading(true);
    try {
      const prepared = preprocess ? await preprocess(file) : file;
      if (prepared.size > policy.maxBytes) {
        setUploadError(policy.sizeError);
        return;
      }
      const result = await uploader(prepared);
      onChange(result.value);
      const previewUrl = prepared.type.startsWith('image/') ? URL.createObjectURL(prepared) : undefined;
      onUploadMetaChange(field.key, { fileName: prepared.name, fileType: prepared.type, previewUrl });
      if (onAutoFill && result.fieldMapping && Object.keys(result.fieldMapping).length > 0) {
        onAutoFill(result.fieldMapping);
      }
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  function handleRemove() {
    onChange('');
    if (meta?.previewUrl) URL.revokeObjectURL(meta.previewUrl);
    onUploadMetaRemove(field.key);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  if (uploading) {
    return (
      <div className="p-6 border border-dashed border-primary/30 rounded-xl text-center">
        <svg className="w-8 h-8 text-primary mx-auto mb-2 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-primary-deep font-medium text-sm">Uploading...</p>
      </div>
    );
  }

  if (meta) {
    return (
      <div className="p-4 border border-green-200 bg-green-50/50 rounded-xl">
        <div className="flex items-center gap-3">
          {meta.previewUrl ? (
            <img src={meta.previewUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center border border-red-100">
              <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{meta.fileName}</p>
            <p className="text-xs text-green-600 font-medium mt-0.5">Uploaded</p>
          </div>
          <button
            onClick={handleRemove}
            className="shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  const openPicker = (capture?: string) => {
    const el = fileInputRef.current;
    if (!el) return;
    if (capture) el.setAttribute('capture', capture);
    else el.removeAttribute('capture');
    el.click();
  };

  const dropzone = (
    <div
      onClick={() => openPicker()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
      }}
      className={`p-6 border border-dashed rounded-xl text-center cursor-pointer transition-colors ${
        dragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary/30'
      }`}
    >
      <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
      <p className="text-gray-600 font-medium text-sm">Click to upload or drag and drop</p>
      <p className="text-gray-500 text-xs mt-1">{policy.helpText}</p>
    </div>
  );

  return (
    <div>
      {captureActions ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:hidden">
            <button
              type="button"
              data-testid="upload-take-photo"
              onClick={() => openPicker('environment')}
              className="flex flex-col items-center justify-center gap-1.5 min-h-[88px] p-3 border border-dashed border-gray-200 rounded-xl text-gray-600 hover:border-primary/30 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="font-medium text-sm">Take photo</span>
            </button>
            <button
              type="button"
              onClick={() => openPicker()}
              className="flex flex-col items-center justify-center gap-1.5 min-h-[88px] p-3 border border-dashed border-gray-200 rounded-xl text-gray-600 hover:border-primary/30 transition-colors"
            >
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <span className="font-medium text-sm">Choose a file</span>
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-2 text-center sm:hidden">{policy.helpText}</p>
          <div className="hidden sm:block">{dropzone}</div>
        </>
      ) : (
        dropzone
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={policy.accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      {uploadError && (
        <p className="text-red-500 text-xs mt-2 font-medium">{uploadError}</p>
      )}
    </div>
  );
}
